'use client';

import { useMemo, useState, type FunctionComponent } from 'react';
import { faArrowsAlt, faCircleInfo, faXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames/bind';
import { formatDistanceToNow } from 'date-fns';
import { ColorsCoralPrimary } from '@fff-web/design-tokens/colors';
import { Heading, Link } from '@fff-web/ui-components';
import type { EnvironmentInfoProps } from './types';
import styles from './EnvironmentInfo.module.scss';

const cx = classNames.bind(styles);

const formatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  hour12: true,
  timeZone: 'UTC'
});

export const EnvironmentInfo: FunctionComponent<EnvironmentInfoProps> = ({
  projectName = '',
  environment,
  version,
  buildDate = new Date().toString(),
  deps,
  docs = [],
}) => {
  const dialogTitle = `${projectName} Environment Info`;
  const buildDateAsDate = new Date(buildDate);
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const formattedBuildDate = formatter.format(buildDateAsDate);
  const timeFromLastBuild = formatDistanceToNow(buildDateAsDate, { addSuffix: true });
  const title = useMemo(() => {
    return !isOpen ? (
      <FontAwesomeIcon
        icon={faCircleInfo}
        color={ColorsCoralPrimary}
        size="2x"
        aria-label={dialogTitle}
      />
    ) : (
      <Heading size="h6">{dialogTitle}</Heading>
    );
  }, [dialogTitle, isOpen]);

  return (
    <div
      className={cx('EnvironmentInfo')}
      role={isOpen ? 'dialog' : 'button'}
      aria-label={isOpen ? '' : 'Open environment information'}
      aria-expanded={isOpen}
      data-expanded={isExpanded}
      data-testid={isExpanded ? 'EnvironmentInfo--expanded' : 'EnvironmentInfo--collapsed'}
      onClick={() => {
        if (!isOpen) {
          setIsOpen(true);
        }
      }}
    >
      <section className={cx('EnvironmentInfo__container')}>
        <div
          className={cx('EnvironmentInfo__scrollable', {
            'EnvironmentInfo__scrollable--open': isOpen,
          })}
        >
          {title}
          {isOpen && (
            <>
              <div className={cx('EnvironmentInfo__content')}>
                <p>
                  Environment: <span>{environment || 'Unknown'}</span>
                </p>
                <p>
                  Version: <span>{version || 'Unknown'}</span>
                </p>
                <p>
                  Build date: <span>{`${formattedBuildDate} (UTC)` || 'Unknown'}</span>
                </p>
                <p>
                  Time from last deployment: <span>{timeFromLastBuild || 'Unknown'}</span>
                </p>
                <p>Internal dependencies: {deps ? '' : 'No internal dependencies'}</p>
                {deps && (
                  <ul className={cx('EnvironmentInfo__deps')}>
                    {deps.split(',').map((dep) => (
                      <li key={dep}>
                        <span>{dep}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {docs.map((doc) => (
                  <p key={doc.url}>
                    {doc.listKey}:{' '}
                    <Link target="_blank" href={doc.url}>
                      {doc.linkText}
                    </Link>
                  </p>
                ))}
              </div>
            </>
          )}
        </div>
        {isOpen && (
          <>
            <button
              onClick={() => {
                setIsOpen(false);
                setIsExpanded(false);
              }}
              className={cx('EnvironmentInfo__close')}
              aria-label="Close environment information"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className={cx('EnvironmentInfo__expand')}
              aria-label="Expand or collapse environment information"
            >
              <FontAwesomeIcon icon={faArrowsAlt} />
            </button>
          </>
        )}
      </section>
    </div>
  );
};
